import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Save, Loader2, BarChart3 } from "lucide-react";

const API = "/api";

const TrackingPanel = ({ onClose }) => {
  const [gtmId, setGtmId] = useState("");
  const [gtmActive, setGtmActive] = useState(false);
  const [pixelId, setPixelId] = useState("");
  const [pixelActive, setPixelActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/tracking`)
      .then((r) => {
        setGtmId(r.data.gtm_id || "");
        setGtmActive(r.data.gtm_active || false);
        setPixelId(r.data.pixel_id || "");
        setPixelActive(r.data.pixel_active || false);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/tracking`, {
        gtm_id: gtmId,
        gtm_active: gtmActive,
        pixel_id: pixelId,
        pixel_active: pixelActive,
      });
      toast.success("Tracking salvato! Le modifiche saranno attive al prossimo caricamento pagina.");
      onClose();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-testid="tracking-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl animate-fade-in-up overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="tracking-title">
                Tracking & Analytics
              </h3>
              <p className="text-xs text-slate-400">Google Tag Manager e Facebook Pixel</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100" data-testid="tracking-close">x</button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className={`rounded-xl border p-4 transition-colors ${gtmActive ? "border-blue-200 bg-blue-50/30" : "border-slate-100"}`} data-testid="tracking-gtm-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-700 font-black text-xs">GTM</span>
                </div>
                <div>
                  <span className="text-sm font-bold text-slate-800">Google Tag Manager</span>
                  <p className="text-[10px] text-slate-400">Gestisci tutti i tag da un unico punto</p>
                </div>
              </div>
              <button
                onClick={() => setGtmActive(!gtmActive)}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${gtmActive ? "bg-blue-500" : "bg-slate-300"}`}
                data-testid="tracking-gtm-toggle"
              >
                <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200"
                  style={{ transform: gtmActive ? "translateX(20px)" : "translateX(2px)" }} />
              </button>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Container ID</label>
              <Input
                placeholder="GTM-XXXXXXX"
                value={gtmId}
                onChange={(e) => setGtmId(e.target.value.toUpperCase())}
                className="h-10 rounded-xl font-mono text-sm"
                data-testid="tracking-gtm-input"
              />
              <p className="text-[10px] text-slate-400 mt-1.5">Lo trovi in Google Tag Manager &gt; Amministrazione &gt; Container ID</p>
            </div>
          </div>

          <div className={`rounded-xl border p-4 transition-colors ${pixelActive ? "border-blue-200 bg-blue-50/30" : "border-slate-100"}`} data-testid="tracking-pixel-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-700 font-black text-[10px]">META</span>
                </div>
                <div>
                  <span className="text-sm font-bold text-slate-800">Facebook Pixel</span>
                  <p className="text-[10px] text-slate-400">Traccia le conversioni delle tue campagne Meta</p>
                </div>
              </div>
              <button
                onClick={() => setPixelActive(!pixelActive)}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${pixelActive ? "bg-blue-500" : "bg-slate-300"}`}
                data-testid="tracking-pixel-toggle"
              >
                <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200"
                  style={{ transform: pixelActive ? "translateX(20px)" : "translateX(2px)" }} />
              </button>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Pixel ID</label>
              <Input
                placeholder="1234567890"
                value={pixelId}
                onChange={(e) => setPixelId(e.target.value.replace(/\D/g, ""))}
                className="h-10 rounded-xl font-mono text-sm"
                data-testid="tracking-pixel-input"
              />
              <p className="text-[10px] text-slate-400 mt-1.5">Lo trovi in Meta Events Manager &gt; Origini dei dati &gt; Pixel ID</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3.5">
            <p className="text-xs text-amber-800 font-medium mb-1">Conversioni automatiche</p>
            <p className="text-[11px] text-amber-600 leading-relaxed">
              Quando un utente compila un form (wizard o landing promo), viene automaticamente inviato un evento di conversione <strong>Lead</strong> sia a GTM (dataLayer) che a Facebook Pixel.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale flex items-center gap-2 disabled:opacity-50"
            data-testid="tracking-save-button"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva Tracking
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrackingPanel;
